<script setup>
import { ref, reactive, computed, onMounted, watch, nextTick } from 'vue'
import { mediaUrl } from '@core/utils/mediaUrl'
import { useRouter } from 'vue-router'
import { storeToRefs } from 'pinia'
import moment from 'moment-timezone'
import axios from '@axios'
import { numberWithCommas, displayPhone } from '@/plugins/misc'
import { useFansStore } from '@/stores/fans.store'
import { useUsersStore } from '@/stores'
import MemberProfileCard from '@core/components/MemberProfileCard.vue'
import PreferredMemberDialog from '@core/components/PreferredMemberDialog.vue'
import CmsText from '@core/components/CmsText.vue'
import BulkMessageDialog from '@/components/admin/BulkMessageDialog.vue'
import FanNoteLine from '@core/components/FanNoteLine.vue'
import RaMessageList from '@core/components/RaMessageList.vue'
import { fanNoteLine } from '@/utils/restaurantHighlights'
import { useDesignSystem } from '@/composables/useDesignSystem'

const router = useRouter()
const { dialogWidth } = useDesignSystem()
const fansStore = useFansStore()
const usersStore = useUsersStore()
const {
  fans, loading, error, filters, sort, hasMore,
  selectedCount, selectedFans, loadingAllFans, allFansError,
  enrichmentRestaurants,
} = storeToRefs(fansStore)

// Independent totals for the Preferred / All chip labels (stay in sync with
// the active restaurant + search context but not the preferred filter itself).
const preferredCount = ref(0)
const allCount = ref(0)

// Members with a highlight RM passed along — the Member Highlights chip.
const highlightsCount = ref(0)

// ─── Full-profile expansion (per fan, lazy-fetched on first open) ───────────
const expandedFans = reactive({}) // { [fanId]: boolean }
const fullFanById = reactive({}) // { [fanId]: User }
const loadingFullFan = reactive({}) // { [fanId]: boolean }

// ─── Bookings expansion (per fan, lazy-fetched on first open) ───────────────
const expandedBookings = reactive({}) // { [fanId]: boolean }
const bookingHistoryById = reactive({}) // { [fanId]: BookingHistory[] }
const loadingBookings = reactive({}) // { [fanId]: boolean }
const expandedBookingRestaurants = reactive({}) // { [fanId:restaurantKey]: boolean }
const bookingsError = reactive({}) // { [fanId]: string | null }

async function toggleBookingsExpansion(fan) {
  const id = fan._id
  const nowExpanded = !expandedBookings[id]
  expandedBookings[id] = nowExpanded
  if (nowExpanded && !bookingHistoryById[id]) {
    loadingBookings[id] = true
    bookingsError[id] = null
    try {
      const { data } = await axios.get(`/restaurant-admin/fans/${id}`)
      bookingHistoryById[id] = Array.isArray(data?.bookingHistory) ? data.bookingHistory : []
    } catch (err) {
      bookingsError[id] = err?.response?.data?.error || 'Failed to load bookings'
    } finally {
      loadingBookings[id] = false
    }
  }
}

function toggleBookingRestaurant(fanId, key) {
  const compositeKey = `${fanId}::${key}`

  expandedBookingRestaurants[compositeKey] = !expandedBookingRestaurants[compositeKey]
}

function isBookingRestaurantExpanded(fanId, key) {
  return Boolean(expandedBookingRestaurants[`${fanId}::${key}`])
}

function bookingSpend(res) {
  const perPerson = Number(res?.minSpendPerPerson) || 0
  const party = Number(res?.partySize) || 0

  return perPerson * party
}

function formatCurrency(val) {
  if (!val) return '$0'

  return '$' + numberWithCommas(Math.round(val))
}

function bookingInsightsFor(fanId) {
  const history = bookingHistoryById[fanId] || []
  const byRestaurant = new Map()
  let totalSpend = 0

  for (const res of history) {
    const name = res.confirmedRestaurant || res.preferredRestaurant || 'Unknown'
    const market = res.market || ''
    const key = `${name}||${market}`
    const spend = bookingSpend(res)

    totalSpend += spend
    if (!byRestaurant.has(key)) {
      byRestaurant.set(key, {
        key,
        restaurantName: name,
        market,
        bookings: [],
        bookingCount: 0,
        totalSpend: 0,
      })
    }
    const entry = byRestaurant.get(key)

    entry.bookings.push({ date: res.date, partySize: res.partySize, spend, status: res.status })
    entry.bookingCount += 1
    entry.totalSpend += spend
  }

  const restaurants = [...byRestaurant.values()].sort((a, b) => b.bookingCount - a.bookingCount)

  restaurants.forEach(r => r.bookings.sort((a, b) => moment(b.date).diff(moment(a.date))))

  return {
    total: history.length,
    totalSpend,
    byRestaurant: restaurants,
  }
}

async function toggleFullProfile(fan) {
  const id = fan._id
  const nowExpanded = !expandedFans[id]
  expandedFans[id] = nowExpanded
  if (nowExpanded && !fullFanById[id]) {
    loadingFullFan[id] = true
    try {
      // Don't trust fetchUserById's return value — when the user isn't already
      // cached it returns the last-pushed entry (which can be a follower from
      // the populated membersFollowed/membersWhoAreFollowing arrays, not the
      // requested user). Always re-lookup by id from the store after await.
      await usersStore.fetchUserById(id)
      const base = usersStore.users.find(u => String(u._id) === String(id)) || null

      // Hide phone number and email until the fan has at least one booking.
      fullFanById[id] = base && !(fan.bookedCount > 0)
        ? { ...base, phoneNumber: null, email: null }
        : base
    } finally {
      loadingFullFan[id] = false
    }
  }
}

// ─── Preferred toggle dialog state ──────────────────────────────────────────
const preferredDialogOpen = ref(false)
const preferredDialogFan = ref(null)

function openPreferredDialog(fan) {
  preferredDialogFan.value = fan
  preferredDialogOpen.value = true
}

// ─── Bulk messaging state ───────────────────────────────────────────────────
const bulkMessageOpen = ref(false)

// Snapshot of fans handed to the composer when it opens — either the
// individually-checked fans or every matching fan ("Message All").
const bulkMessageFans = ref([])

// "Message All": pull every fan matching the current filters and open the
// composer. Leaves the individual-checkbox selection untouched.
async function messageAll() {
  const all = await fansStore.fetchAllMatchingFans()
  if (all.length === 0) return
  bulkMessageFans.value = all
  bulkMessageOpen.value = true
}

// "Message Selected Members": open the composer with the checked fans.
function messageSelected() {
  if (selectedCount.value === 0) return
  bulkMessageFans.value = selectedFans.value
  bulkMessageOpen.value = true
}

// The single bulk-action button routes to whichever flow is active: any
// individual selection → "selected"; otherwise → "all".
function openBulkMessage() {
  if (selectedCount.value > 0) {
    messageSelected()
  } else {
    messageAll()
  }
}

// "Message All" needs a single sender restaurant — it stays disabled until the
// admin narrows the restaurant filter. "Message Selected Members" is unaffected
// (the composer resolves a sender from the selected fans' favorited restaurants).
const messageAllDisabled = computed(() =>
  selectedCount.value === 0 && !filters.value.restaurantId,
)

// ─── Messaging composer state ───────────────────────────────────────────────
const messageDialogOpen = ref(false)
const messageDialogFan = ref(null)
const messageText = ref('')
const selectedRestaurantId = ref(null)
const messageSending = ref(false)
const messageError = ref(null)

// In-scope restaurants this fan has favorited — eligible senders for the
// conversation. Mirrors the per-fan detail page composer rule.
function messagingEligibleRestaurantsFor(fan) {
  const favIds = new Set((fan?.favoritedRestaurantIds || []).map(id => String(id)))

  return restaurants.value.filter(r => favIds.has(String(r._id)))
}

const dialogEligibleRestaurants = computed(() =>
  messageDialogFan.value ? messagingEligibleRestaurantsFor(messageDialogFan.value) : [],
)

function openMessageDialog(fan) {
  messageDialogFan.value = fan
  messageText.value = ''
  messageError.value = null
  const eligible = messagingEligibleRestaurantsFor(fan)
  selectedRestaurantId.value = eligible.length === 1 ? eligible[0]._id : null
  messageDialogOpen.value = true
  loadMessageHistory()
}

/**
 * The conversation so far, above the box (Kapil, 2026-09-25: "Send message
 * to Sid Bala should show the message history") — the thread with the
 * restaurant it is sent from, drawn as Messages draws it, their highlight
 * included. A later pick of restaurant or dialog wins over an earlier load.
 */
const messageHistory = ref([])
const messageHistoryLoading = ref(false)
const messageHistoryEl = ref(null)
let messageHistoryFor = ''

async function loadMessageHistory() {
  const fan = messageDialogFan.value
  const restaurantId = selectedRestaurantId.value
  messageHistory.value = []
  const key = fan?._id && restaurantId ? `${fan._id}:${restaurantId}` : ''
  messageHistoryFor = key
  if (!key) return
  messageHistoryLoading.value = true
  try {
    const { data } = await axios.get(`/restaurant-admin/fans/${fan._id}`)
    const conv = (data?.messagingConversationIds || []).find(c => String(c.restaurantId) === String(restaurantId))
    if (conv && messageHistoryFor === key) {
      const res = await axios.get(`/messaging/restaurantAdmin/conversations/${conv.conversationId}`)
      if (messageHistoryFor === key) messageHistory.value = res.data?.messages || []
    }
  } catch {
    if (messageHistoryFor === key) messageHistory.value = []
  } finally {
    if (messageHistoryFor === key) {
      messageHistoryLoading.value = false
      // Newest at the bottom, next to the box.
      await nextTick()
      if (messageHistoryEl.value) messageHistoryEl.value.scrollTop = messageHistoryEl.value.scrollHeight
    }
  }
}

watch(selectedRestaurantId, () => {
  if (messageDialogOpen.value) loadMessageHistory()
})

const messageHistoryRestaurantName = computed(() =>
  restaurants.value.find(r => String(r._id) === String(selectedRestaurantId.value))?.name || '')

async function sendMessage() {
  const fan = messageDialogFan.value
  if (!fan?._id || !messageText.value.trim()) return
  const restaurantId = selectedRestaurantId.value
  if (!restaurantId) {
    messageError.value = 'Please select a restaurant to send from.'

    return
  }
  messageSending.value = true
  messageError.value = null
  try {
    await axios.post('/messaging/restaurantAdmin/conversations', {
      memberUserId: fan._id,
      restaurantId,
      text: messageText.value.trim(),
    })

    messageDialogOpen.value = false
  } catch (err) {
    messageError.value = err?.response?.data?.message || 'Failed to send message'
  } finally {
    messageSending.value = false
  }
}

// ─── Guest notes dialog state ───────────────────────────────────────────────
// Per-restaurant guest notes — visible only to the admin team, never to the
// member. Mirrors the editor in /admin/fans/[memberUserId].vue but presented
// as a dialog so admins can review/edit notes without leaving the list view.
const GUEST_NOTE_MAX_LENGTH = 2000
const guestNotesDialogOpen = ref(false)
const guestNotesDialogFan = ref(null)
const guestNotesLoading = ref(false)
const guestNotesError = ref(null)
const guestNotesByRestaurant = ref({}) // { [restaurantId]: note record }
const guestNoteDrafts = ref({}) // { [restaurantId]: string }
const guestNoteSaving = ref({}) // { [restaurantId]: boolean }
const guestNoteRowError = ref({}) // { [restaurantId]: string | null }
const editingGuestNotes = ref({}) // { [restaurantId]: boolean }
const guestNoteAddMenuOpen = ref(false)

function guestNoteFor(restaurantId) {
  return guestNotesByRestaurant.value[String(restaurantId)] || null
}

// Reuse the messaging eligibility list — notes are also scoped to in-scope
// restaurants the fan has favorited.
const guestNotesEligibleRestaurants = computed(() =>
  guestNotesDialogFan.value
    ? messagingEligibleRestaurantsFor(guestNotesDialogFan.value)
    : [],
)

const visibleGuestNoteRows = computed(() =>
  guestNotesEligibleRestaurants.value.filter(r =>
    editingGuestNotes.value[r._id] || guestNoteFor(r._id),
  ),
)

const restaurantsWithoutGuestNote = computed(() =>
  guestNotesEligibleRestaurants.value.filter(r =>
    !editingGuestNotes.value[r._id] && !guestNoteFor(r._id),
  ),
)

function indexGuestNotes(notes) {
  const map = {}
  const drafts = {}
  ;(Array.isArray(notes) ? notes : []).forEach(n => {
    const key = String(n.restaurantId)

    map[key] = n
    drafts[key] = n.note || ''
  })
  guestNotesByRestaurant.value = map
  guestNoteDrafts.value = drafts

  // Keep the list row's "Guest notes" badge in sync with the notes actually
  // loaded for this fan (runs on open and after each save/remove).
  if (guestNotesDialogFan.value) {
    guestNotesDialogFan.value.guestNoteCount = Object.keys(map).length
  }
}

async function openGuestNotesDialog(fan) {
  guestNotesDialogFan.value = fan
  guestNotesDialogOpen.value = true
  guestNotesLoading.value = true
  guestNotesError.value = null
  guestNotesByRestaurant.value = {}
  guestNoteDrafts.value = {}
  editingGuestNotes.value = {}
  guestNoteRowError.value = {}
  guestNoteAddMenuOpen.value = false
  try {
    const { data } = await axios.get(`/restaurant-admin/fans/${fan._id}`)

    indexGuestNotes(data?.guestNotes)
  } catch (err) {
    guestNotesError.value = err?.response?.data?.error || 'Failed to load guest notes'
  } finally {
    guestNotesLoading.value = false
  }
}

function startEditingGuestNote(restaurantId) {
  guestNoteDrafts.value = {
    ...guestNoteDrafts.value,
    [restaurantId]: guestNoteFor(restaurantId)?.note || '',
  }
  guestNoteRowError.value = { ...guestNoteRowError.value, [restaurantId]: null }
  editingGuestNotes.value = { ...editingGuestNotes.value, [restaurantId]: true }
}

function cancelEditingGuestNote(restaurantId) {
  guestNoteDrafts.value = {
    ...guestNoteDrafts.value,
    [restaurantId]: guestNoteFor(restaurantId)?.note || '',
  }
  guestNoteRowError.value = { ...guestNoteRowError.value, [restaurantId]: null }
  editingGuestNotes.value = { ...editingGuestNotes.value, [restaurantId]: false }
}

function beginAddingGuestNote(restaurantId) {
  startEditingGuestNote(restaurantId)
  guestNoteAddMenuOpen.value = false
}

function guestNoteHasChange(restaurantId) {
  const draft = (guestNoteDrafts.value[restaurantId] || '').trim()
  const saved = guestNoteFor(restaurantId)?.note || ''

  return draft !== saved
}

async function saveGuestNote(restaurantId) {
  const fan = guestNotesDialogFan.value
  if (!fan?._id) return
  guestNoteSaving.value = { ...guestNoteSaving.value, [restaurantId]: true }
  guestNoteRowError.value = { ...guestNoteRowError.value, [restaurantId]: null }
  try {
    await axios.put(`/restaurant-admin/fans/${fan._id}/notes`, {
      restaurantId,
      note: guestNoteDrafts.value[restaurantId] || '',
    })

    // Re-fetch so each row shows fresh updatedAt / updatedByName metadata.
    const { data } = await axios.get(`/restaurant-admin/fans/${fan._id}`)

    indexGuestNotes(data?.guestNotes)
    editingGuestNotes.value = { ...editingGuestNotes.value, [restaurantId]: false }
  } catch (err) {
    guestNoteRowError.value = {
      ...guestNoteRowError.value,
      [restaurantId]: err?.response?.data?.error || 'Failed to save note',
    }
  } finally {
    guestNoteSaving.value = { ...guestNoteSaving.value, [restaurantId]: false }
  }
}

async function removeGuestNote(restaurantId) {
  guestNoteDrafts.value = { ...guestNoteDrafts.value, [restaurantId]: '' }
  await saveGuestNote(restaurantId)
}

function formatNoteUpdated(dateStr) {
  if (!dateStr) return ''
  try {
    return new Date(dateStr).toLocaleString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit',
    })
  } catch {
    return dateStr
  }
}

function onPreferredUpdated({ memberUserId, isPreferred, restaurantIds, groupIds }) {
  if (!memberUserId) return
  fansStore.updateFan(memberUserId, {
    isPreferred,
    preferredAtRestaurantIds: restaurantIds,
    preferredScopeUserGroupIds: groupIds,
  })
  loadCounts()
}

// Multi-restaurant scope
const restaurants = ref([])
const loadingScope = ref(false)
const hasMultipleRestaurants = computed(() => restaurants.value.length > 1)

// Restaurant picker sheet
const restaurantPickerOpen = ref(false)

// Debounced search
const searchInput = ref('')
let searchTimer = null

function onSearchInput() {
  clearTimeout(searchTimer)
  searchTimer = setTimeout(() => {
    fansStore.setSearch(searchInput.value)
    fansStore.fetchFans(true)
    loadCounts()
  }, 300)
}

// Active filter tab: 'all' | 'preferred'
// Member Highlights is a filter like Preferred Members (Kapil, 2026-09-25:
// "same display as on the All Fans but with just the members who have them").
const activeFilter = computed(() => {
  if (filters.value.highlightsOnly) return 'highlights'

  return filters.value.preferredOnly ? 'preferred' : 'all'
})

function setFilter(type) {
  fansStore.setFilters({ preferredOnly: type === 'preferred', highlightsOnly: type === 'highlights' })
  fansStore.fetchFans(true)
}

function setRestaurant(restaurantId) {
  fansStore.setFilters({ restaurantId: restaurantId || null })
  restaurantPickerOpen.value = false
  fansStore.fetchFans(true)
}

function setSort(value) {
  fansStore.setSort(value)
  fansStore.fetchFans(true)
}

function openFan(fan) {
  router.push(`/admin/fans/${fan._id}`)
}

function onCardClick(fan, event) {
  // Don't navigate when clicking inside the expanded profile, contact links,
  // the view-full-profile toggle, or the bookings-expansion area — those
  // have their own behaviors.
  if (event.target.closest('.admin-fans__checkbox')) return
  if (event.target.closest('.admin-fans__profile-expanded')) return
  if (event.target.closest('.admin-fans__contact-item')) return
  if (event.target.closest('.admin-fans__profile-toggle')) return
  if (event.target.closest('.admin-fans__bookings-trigger')) return
  if (event.target.closest('.admin-fans__bookings-panel')) return
  if (event.target.closest('.admin-fans__message-toggle')) return
  if (event.target.closest('.admin-fans__notes-toggle')) return
  openFan(fan)
}

function memberInitials(fan) {
  const first = fan.firstName?.[0] || ''
  const last = fan.lastName?.[0] || ''

  return (first + last).toUpperCase() || '?'
}

// Matches ReservationCardAdmin.vue's showPlatinumBadge logic — fire on any
// Platinum-adjacent state so Trial members also get the (Platinum) label.
function isPlatinumLike(fan) {
  return Boolean(
    fan?.isPlatinum
    || fan?.membershipType === 'Platinum'
    || fan?.membershipType === 'Trial',
  )
}

function favoritedRestaurantNames(fan) {
  const ids = Array.isArray(fan.favoritedRestaurantIds) ? fan.favoritedRestaurantIds : []
  if (ids.length === 0) return []

  // "Fan of" widens to the whole restaurant group, so resolve names from the
  // directly-managed restaurants AND the group enrichment scope. The restaurant
  // filter picker and messaging eligibility still use `restaurants` only.
  const byId = new Map(restaurants.value.map(r => [String(r._id), r.name]))
  enrichmentRestaurants.value.forEach(r => byId.set(String(r._id), r.name))

  return ids
    .map(id => byId.get(String(id)))
    .filter(Boolean)
}

function fanOfLine(fan) {
  const favNames = favoritedRestaurantNames(fan)
  if (favNames.length === 0) return ''

  return `Fan: ${favNames.join(', ')}`
}

function bookingsLine(fan) {
  if (!(fan.bookedCount > 0)) return ''

  return `Bookings via RM: ${fan.bookedCount}`
}

function hasMetaLines(fan) {
  return Boolean(fanOfLine(fan)) || Boolean(bookingsLine(fan)) || Boolean(fanNoteLine(fan))
}

// The restaurant a fan's note is about, for the private-note hint: the one
// chosen, or the team's only one.
const noteRestaurantName = computed(() => {
  if (filters.value.restaurantId) return restaurants.value.find(r => String(r._id) === String(filters.value.restaurantId))?.name || ''

  return restaurants.value.length === 1 ? restaurants.value[0].name : ''
})

// Which restaurant got a highlight — said only when the team runs several.
function highlightRestaurantName(fan) {
  if (!hasMultipleRestaurants.value || filters.value.restaurantId) return ''

  return restaurants.value.find(r => String(r._id) === String(fan?.highlight?.restaurantId))?.name || ''
}

const selectedRestaurantName = computed(() => {
  if (!filters.value.restaurantId) return null
  
  return restaurants.value.find(r => r._id === filters.value.restaurantId)?.name || null
})

const sortOptions = [
  { title: 'Member Tier', value: 'tier' },
  { title: 'Last Active', value: 'lastActive' },
  { title: 'Alphabetical', value: 'alpha' },
  { title: 'Recently Preferred', value: 'preferredDesc' },
  { title: 'Most Favorited', value: 'favoriteCountDesc' },
  { title: 'Recently Booked', value: 'lastBookingDesc' },
]

const sortLabel = computed(() => {
  return sortOptions.find(o => o.value === sort.value)?.title || 'Member Tier'
})

async function loadScope() {
  loadingScope.value = true
  try {
    const { data } = await axios.get('/user/me/restaurant-scope')
    restaurants.value = data.restaurants || []

    // With a single restaurant in scope there's nothing to pick — default the
    // filter to it so the list is pre-scoped (the picker chip is hidden too).
    if (restaurants.value.length === 1 && !filters.value.restaurantId) {
      fansStore.setFilters({ restaurantId: restaurants.value[0]._id })
    }
  } catch {
    restaurants.value = []
  } finally {
    loadingScope.value = false
  }
}

async function loadCounts() {
  try {
    const baseParams = { limit: 1, skip: 0 }
    if (filters.value.restaurantId) baseParams.restaurantId = filters.value.restaurantId
    const term = searchInput.value?.trim()
    if (term) baseParams.search = term

    const [allRes, prefRes, highlightsRes] = await Promise.all([
      axios.get('/restaurant-admin/fans', { params: baseParams }),
      axios.get('/restaurant-admin/fans', { params: { ...baseParams, preferredOnly: 'true' } }),
      axios.get('/restaurant-admin/fans', { params: { ...baseParams, highlightsOnly: 'true' } }),
    ])
    allCount.value = allRes.data?.total || 0
    preferredCount.value = prefRes.data?.total || 0
    highlightsCount.value = highlightsRes.data?.total || 0
  } catch {
    // best-effort; leave previous values
  }
}

onMounted(async () => {
  await loadScope()
  await Promise.all([fansStore.fetchFans(true), loadCounts()])
})

watch(
  () => filters.value.restaurantId,
  () => {
    fansStore.fetchFans(true)
    loadCounts()
  },
)
</script>

<template>
  <div class="admin-fans">
    <!-- 1. Restaurant dropdown -->
    <div
      v-if="hasMultipleRestaurants"
      class="admin-fans__chips admin-fans__chips--restaurant"
    >
      <VChip
        :color="filters.restaurantId ? 'primary-dark' : undefined"
        :variant="filters.restaurantId ? 'flat' : 'outlined'"
        class="admin-fans__chip admin-fans_main_chip"
        append-icon="mdi-chevron-down"
        @click="restaurantPickerOpen = true"
      >
        {{ selectedRestaurantName || 'All Restaurants' }}
      </VChip>
    </div>

    <!-- 2. Preferred Members / All Fans counts -->
    <div class="admin-fans__chips">
      <VChip
        :color="activeFilter === 'preferred' ? 'primary-dark' : undefined"
        :variant="activeFilter === 'preferred' ? 'flat' : 'outlined'"
        class="admin-fans__chip"
        @click="setFilter('preferred')"
      >
        Preferred Members - {{ preferredCount }}
      </VChip>

      <VChip
        :color="activeFilter === 'all' ? 'primary-dark' : undefined"
        :variant="activeFilter === 'all' ? 'flat' : 'outlined'"
        class="admin-fans__chip"
        @click="setFilter('all')"
      >
        All Fans - {{ allCount }}
      </VChip>

      <VChip
        :color="activeFilter === 'highlights' ? 'primary-dark' : undefined"
        :variant="activeFilter === 'highlights' ? 'flat' : 'outlined'"
        class="admin-fans__chip"
        @click="setFilter('highlights')"
      >
        Member Highlights - {{ highlightsCount }}
      </VChip>
    </div>

    <!-- 3. Search bar (matches MainSearch flat material styling) -->
    <VTextField
      v-model="searchInput"
      placeholder="Search by fan name or company"
      prepend-inner-icon="mdi-magnify"
      variant="solo-filled"
      flat
      rounded="pill"
      density="comfortable"
      clearable
      hide-details
      class="admin-fans__search"
      style="font-size: 16px;"
      @input="onSearchInput"
      @click:clear="() => { searchInput = ''; fansStore.setSearch(''); fansStore.fetchFans(true); loadCounts() }"
    />

    <!-- Error -->
    <VAlert
      v-if="error"
      type="error"
      variant="tonal"
      closable
      class="mb-4"
    >
      {{ error }}
    </VAlert>

    <!-- Bulk message action -->
    <div
      v-if="fans.length > 0"
      class="admin-fans__bulk-action"
    >
      <div class="admin-fans__bulk-action-row">
        <VBtn
          color="primary-dark"
          variant="flat"
          prepend-icon="mdi-message-text-outline"
          :loading="loadingAllFans"
          :disabled="messageAllDisabled"
          class="admin-fans__bulk-action-btn"
          @click="openBulkMessage"
        >
          {{ selectedCount > 0 ? 'Message Selected Members' : 'Message All' }}
        </VBtn>
        <VBtn
          v-if="selectedCount > 0"
          variant="text"
          size="small"
          class="admin-fans__bulk-action-clear"
          @click="fansStore.clearSelection()"
        >
          Clear ({{ selectedCount }})
        </VBtn>
      </div>
      <p
        v-if="messageAllDisabled"
        class="admin-fans__bulk-action-hint"
      >
        Select a restaurant to message all
      </p>
      <VAlert
        v-if="allFansError"
        type="error"
        variant="tonal"
        density="compact"
        class="admin-fans__bulk-action-error"
      >
        {{ allFansError }}
      </VAlert>
    </div>

    <!-- Loading skeleton -->
    <div
      v-if="loading && fans.length === 0"
      class="admin-fans__grid"
    >
      <div
        v-for="i in 6"
        :key="i"
        class="admin-fans__card admin-fans__card--skeleton"
      >
        <div class="admin-fans__card-body">
          <div class="admin-fans__avatar-wrap">
            <VSkeletonLoader type="avatar" />
          </div>
          <VSkeletonLoader
            type="heading"
            class="admin-fans__skeleton-line"
          />
          <VSkeletonLoader
            type="text"
            class="admin-fans__skeleton-line"
          />
          <VSkeletonLoader
            type="text"
            class="admin-fans__skeleton-line"
          />
        </div>
      </div>
    </div>

    <!-- Empty state -->
    <div
      v-else-if="!loading && fans.length === 0"
      class="admin-fans__empty"
    >
      <VIcon
        size="48"
        color="medium-emphasis"
        class="mb-3"
      >
        mdi-account-heart-outline
      </VIcon>
      <div class="admin-fans__empty-title">
        No fans yet
      </div>
      <div class="admin-fans__empty-sub">
        Members who favorite your restaurant will appear here.
      </div>
    </div>

    <!-- Fan grid -->
    <div
      v-else
      class="admin-fans__grid"
    >
      <div
        v-for="fan in fans"
        :key="fan._id"
        class="admin-fans__card"
        :class="{ 'admin-fans__card--selected': fansStore.isSelected(fan._id) }"
        role="button"
        tabindex="0"
        @click="onCardClick(fan, $event)"
        @keydown.enter.self="onCardClick(fan, $event)"
      >
        <!-- Selection checkbox -->
        <div
          class="admin-fans__checkbox"
          @click.stop="fansStore.toggleSelect(fan)"
        >
          <VCheckbox
            :model-value="fansStore.isSelected(fan._id)"
            density="compact"
            hide-details
            color="primary-dark"
            :aria-label="`Select ${fan.firstName} ${fan.lastName}`"
            @update:model-value="fansStore.toggleSelect(fan)"
          />
        </div>

        <!-- Content column -->
        <div class="admin-fans__card-body">
          <!-- Expanded full profile (lazy-loaded) -->
          <template v-if="expandedFans[fan._id]">
            <div
              v-if="loadingFullFan[fan._id] && !fullFanById[fan._id]"
              class="admin-fans__profile-loading"
            >
              <VProgressCircular
                indeterminate
                color="primary-dark"
                size="28"
              />
            </div>
            <MemberProfileCard
              v-else
              :member="fullFanById[fan._id] || fan"
              show-stats
              class="admin-fans__profile-expanded"
            />

            <!-- Meta counts (always visible) -->
            <div
              v-if="hasMetaLines(fan)"
              class="admin-fans__meta-group"
            >
              <p
                v-if="fanOfLine(fan)"
                class="admin-fans__meta"
              >
                {{ fanOfLine(fan) }}
              </p>
              <template v-if="bookingsLine(fan)">
                <button
                  type="button"
                  class="admin-fans__bookings-trigger"
                  :aria-expanded="Boolean(expandedBookings[fan._id])"
                  :aria-controls="`bookings-panel-${fan._id}`"
                  @click.stop="toggleBookingsExpansion(fan)"
                >
                  <span>{{ bookingsLine(fan) }}</span>
                  <VIcon size="14">
                    {{ expandedBookings[fan._id] ? 'mdi-chevron-up' : 'mdi-chevron-down' }}
                  </VIcon>
                </button>
                <VExpandTransition>
                  <div
                    v-if="expandedBookings[fan._id]"
                    :id="`bookings-panel-${fan._id}`"
                    class="admin-fans__bookings-panel"
                    @click.stop
                  >
                    <div
                      v-if="loadingBookings[fan._id]"
                      class="admin-fans__bookings-loading"
                    >
                      <VProgressCircular
                        indeterminate
                        size="20"
                        color="primary-dark"
                      />
                    </div>
                    <div
                      v-else-if="bookingsError[fan._id]"
                      class="admin-fans__bookings-error"
                    >
                      {{ bookingsError[fan._id] }}
                    </div>
                    <template v-else>
                      <div class="admin-fans__bookings-summary">
                        <div class="admin-fans__bookings-summary-stat">
                          <div class="admin-fans__bookings-stat-value">
                            {{ bookingInsightsFor(fan._id).total }}
                          </div>
                          <div class="admin-fans__bookings-stat-label">
                            Bookings
                          </div>
                        </div>
                        <div class="admin-fans__bookings-summary-stat">
                          <div class="admin-fans__bookings-stat-value">
                            {{ formatCurrency(bookingInsightsFor(fan._id).totalSpend) }}
                          </div>
                          <div class="admin-fans__bookings-stat-label">
                            Guaranteed F&amp;B Spend
                          </div>
                        </div>
                      </div>
                      <div
                        v-for="r in bookingInsightsFor(fan._id).byRestaurant"
                        :key="r.key"
                        class="admin-fans__bookings-restaurant"
                      >
                        <button
                          type="button"
                          class="admin-fans__bookings-restaurant-header"
                          :aria-expanded="isBookingRestaurantExpanded(fan._id, r.key)"
                          @click.stop="toggleBookingRestaurant(fan._id, r.key)"
                        >
                          <div class="admin-fans__bookings-restaurant-info">
                            <div class="admin-fans__bookings-restaurant-name">
                              {{ r.restaurantName }}
                            </div>
                            <div
                              v-if="r.market"
                              class="admin-fans__bookings-restaurant-market"
                            >
                              {{ r.market }}
                            </div>
                          </div>
                          <div class="admin-fans__bookings-restaurant-totals">
                            <div class="admin-fans__bookings-restaurant-count">
                              {{ r.bookingCount }} {{ r.bookingCount === 1 ? 'booking' : 'bookings' }}
                            </div>
                            <div class="admin-fans__bookings-restaurant-spend">
                              {{ formatCurrency(r.totalSpend) }}
                            </div>
                          </div>
                          <VIcon
                            size="16"
                            class="admin-fans__bookings-restaurant-chevron"
                          >
                            {{ isBookingRestaurantExpanded(fan._id, r.key) ? 'mdi-chevron-up' : 'mdi-chevron-down' }}
                          </VIcon>
                        </button>
                        <VExpandTransition>
                          <div
                            v-if="isBookingRestaurantExpanded(fan._id, r.key)"
                            class="admin-fans__bookings-rows"
                          >
                            <div
                              v-for="(b, idx) in r.bookings"
                              :key="idx"
                              class="admin-fans__bookings-row"
                            >
                              <span class="admin-fans__bookings-row-date">
                                {{ b.date ? moment(b.date).format('MM-DD-YY') : '—' }}
                              </span>
                              <span
                                v-if="b.partySize"
                                class="admin-fans__bookings-row-party"
                              >Party of {{ b.partySize }}</span>
                              <span
                                v-else
                                class="admin-fans__bookings-row-party"
                              />
                              <span class="admin-fans__bookings-row-spend">
                                {{ formatCurrency(b.spend) }}
                              </span>
                            </div>
                          </div>
                        </VExpandTransition>
                      </div>
                    </template>
                  </div>
                </VExpandTransition>
              </template>
              <!--
                Under Bookings via RM (Kapil, 2026-09-25): their highlight, or
                the filled note icon when they keep a note about you privately.
              -->
              <FanNoteLine
                :fan="fan"
                :restaurant-name="noteRestaurantName"
                :highlight-restaurant-name="highlightRestaurantName(fan)"
              />
            </div>

            <!-- Collapse profile -->
            <div class="admin-fans__toggle-row">
              <button
                type="button"
                class="admin-fans__profile-toggle"
                :aria-expanded="true"
                @click.stop="toggleFullProfile(fan)"
              >
                <span>Collapse profile</span>
                <VIcon size="14">
                  mdi-chevron-up
                </VIcon>
              </button>
            </div>

            <!-- Bottom action row -->
            <div class="admin-fans__toggle-row admin-fans__action-row">
              <button
                type="button"
                class="admin-fans__profile-toggle admin-fans__preferred-toggle"
                :class="{ 'admin-fans__preferred-toggle--active': fan.isPreferred }"
                :aria-label="fan.isPreferred ? 'Manage Preferred status' : 'Mark as Preferred'"
                @click.stop="openPreferredDialog(fan)"
              >
                <VIcon size="14">
                  {{ fan.isPreferred ? 'mdi-star' : 'mdi-star-outline' }}
                </VIcon>
                <span>{{ fan.isPreferred ? 'Preferred' : 'Mark Preferred' }}</span>
              </button>
              <button
                v-if="messagingEligibleRestaurantsFor(fan).length > 0"
                type="button"
                class="admin-fans__profile-toggle admin-fans__message-toggle"
                aria-label="Send message"
                @click.stop="openMessageDialog(fan)"
              >
                <VIcon size="16">
                  mdi-message-text-outline
                </VIcon>
              </button>
              <button
                v-if="messagingEligibleRestaurantsFor(fan).length > 0"
                type="button"
                class="admin-fans__profile-toggle admin-fans__notes-toggle"
                aria-label="Manage guest notes"
                @click.stop="openGuestNotesDialog(fan)"
              >
                <VIcon size="16">
                  {{ (fan.guestNoteCount || 0) > 0 ? 'mdi-note-text' : 'mdi-note-text-outline' }}
                </VIcon>
                <span>Guest notes</span>
                <span
                  v-if="(fan.guestNoteCount || 0) > 0"
                  class="admin-fans__notes-count"
                >
                  {{ fan.guestNoteCount }}
                </span>
              </button>
            </div>
          </template>

          <!-- Compact view (matches reservations-review.vue member block) -->
          <template v-else>
            <div class="admin-fans__member-compact">
              <component
                :is="fan.profileId ? 'a' : 'div'"
                v-bind="fan.profileId ? {
                  href: `/m/${fan.profileId}`,
                  target: '_blank',
                  rel: 'noopener noreferrer',
                  'aria-label': 'View member profile',
                } : {}"
                class="admin-fans__avatar-slot"
                @click.stop
              >
                <VAvatar
                  v-if="fan.profileImageId"
                  :size="36"
                  class="admin-fans__avatar-small"
                >
                  <VImg
                    :src="mediaUrl(fan.profileImageId, 'thumb')"
                    :alt="`${fan.firstName} ${fan.lastName}`"
                  />
                </VAvatar>
                <VAvatar
                  v-else
                  :size="36"
                  class="admin-fans__avatar-small admin-fans__avatar-small--initials"
                >
                  <span class="admin-fans__avatar-small-text">{{ memberInitials(fan) }}</span>
                </VAvatar>
              </component>

              <div class="admin-fans__member-info">
                <div class="admin-fans__member-name-row">
                  <a
                    v-if="fan.profileId"
                    :href="`/m/${fan.profileId}`"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="admin-fans__member-link"
                    @click.stop
                  >{{ fan.firstName }} {{ fan.lastName }}</a>
                  <span
                    v-else
                    class="admin-fans__member-name-static"
                  >{{ fan.firstName }} {{ fan.lastName }}</span>
                  <span
                    v-if="fan.verified || isPlatinumLike(fan)"
                    class="admin-fans__member-badges"
                  >
                    <VIcon
                      v-if="fan.verified"
                      size="14"
                      class="admin-fans__member-verified"
                      aria-label="Verified member"
                    >
                      material-symbols:verified
                    </VIcon>
                    <span
                      v-if="isPlatinumLike(fan)"
                      class="admin-fans__platinum-label"
                    >
                      (Platinum)
                    </span>
                  </span>
                </div>
                <div
                  v-if="fan.title || fan.company"
                  class="admin-fans__member-subtitle"
                >
                  {{ [fan.title, fan.company].filter(Boolean).join(' · ') }}
                </div>
              </div>
            </div>

            <!-- View full profile -->
            <div class="admin-fans__toggle-row">
              <button
                type="button"
                class="admin-fans__profile-toggle"
                :aria-expanded="false"
                @click.stop="toggleFullProfile(fan)"
              >
                <span>View full profile</span>
                <VIcon size="14">
                  mdi-chevron-down
                </VIcon>
              </button>
            </div>

            <!-- Contact info (phone shown only when the fan has ≥1 booking) -->
            <div
              v-if="(fan.phoneNumber || fan.email) && fan.bookedCount > 0"
              class="admin-fans__contact"
            >
              <a
                v-if="fan.phoneNumber && fan.bookedCount > 0"
                class="admin-fans__contact-item"
                :href="`tel:${fan.phoneNumber}`"
                @click.stop
              >
                <VIcon
                  size="14"
                  class="admin-fans__contact-icon"
                >
                  mdi-phone-outline
                </VIcon>
                <span>{{ displayPhone(fan.phoneNumber) }}</span>
              </a>
              <a
                v-if="fan.email && fan.bookedCount > 0"
                class="admin-fans__contact-item"
                :href="`mailto:${fan.email}`"
                @click.stop
              >
                <VIcon
                  size="14"
                  class="admin-fans__contact-icon"
                >
                  mdi-email-outline
                </VIcon>
                <span>{{ fan.email }}</span>
              </a>
            </div>

            <!-- Meta counts -->
            <div
              v-if="hasMetaLines(fan)"
              class="admin-fans__meta-group"
            >
              <p
                v-if="fanOfLine(fan)"
                class="admin-fans__meta"
              >
                {{ fanOfLine(fan) }}
              </p>
              <template v-if="bookingsLine(fan)">
                <button
                  type="button"
                  class="admin-fans__bookings-trigger"
                  :aria-expanded="Boolean(expandedBookings[fan._id])"
                  :aria-controls="`bookings-panel-${fan._id}`"
                  @click.stop="toggleBookingsExpansion(fan)"
                >
                  <span>{{ bookingsLine(fan) }}</span>
                  <VIcon size="14">
                    {{ expandedBookings[fan._id] ? 'mdi-chevron-up' : 'mdi-chevron-down' }}
                  </VIcon>
                </button>
                <VExpandTransition>
                  <div
                    v-if="expandedBookings[fan._id]"
                    :id="`bookings-panel-${fan._id}`"
                    class="admin-fans__bookings-panel"
                    @click.stop
                  >
                    <div
                      v-if="loadingBookings[fan._id]"
                      class="admin-fans__bookings-loading"
                    >
                      <VProgressCircular
                        indeterminate
                        size="20"
                        color="primary-dark"
                      />
                    </div>
                    <div
                      v-else-if="bookingsError[fan._id]"
                      class="admin-fans__bookings-error"
                    >
                      {{ bookingsError[fan._id] }}
                    </div>
                    <template v-else>
                      <div class="admin-fans__bookings-summary">
                        <div class="admin-fans__bookings-summary-stat">
                          <div class="admin-fans__bookings-stat-value">
                            {{ bookingInsightsFor(fan._id).total }}
                          </div>
                          <div class="admin-fans__bookings-stat-label">
                            Bookings
                          </div>
                        </div>
                        <div class="admin-fans__bookings-summary-stat">
                          <div class="admin-fans__bookings-stat-value">
                            {{ formatCurrency(bookingInsightsFor(fan._id).totalSpend) }}
                          </div>
                          <div class="admin-fans__bookings-stat-label">
                            Guaranteed F&amp;B Spend
                          </div>
                        </div>
                      </div>
                      <div
                        v-for="r in bookingInsightsFor(fan._id).byRestaurant"
                        :key="r.key"
                        class="admin-fans__bookings-restaurant"
                      >
                        <button
                          type="button"
                          class="admin-fans__bookings-restaurant-header"
                          :aria-expanded="isBookingRestaurantExpanded(fan._id, r.key)"
                          @click.stop="toggleBookingRestaurant(fan._id, r.key)"
                        >
                          <div class="admin-fans__bookings-restaurant-info">
                            <div class="admin-fans__bookings-restaurant-name">
                              {{ r.restaurantName }}
                            </div>
                            <div
                              v-if="r.market"
                              class="admin-fans__bookings-restaurant-market"
                            >
                              {{ r.market }}
                            </div>
                          </div>
                          <div class="admin-fans__bookings-restaurant-totals">
                            <div class="admin-fans__bookings-restaurant-count">
                              {{ r.bookingCount }} {{ r.bookingCount === 1 ? 'booking' : 'bookings' }}
                            </div>
                            <div class="admin-fans__bookings-restaurant-spend">
                              {{ formatCurrency(r.totalSpend) }}
                            </div>
                          </div>
                          <VIcon
                            size="16"
                            class="admin-fans__bookings-restaurant-chevron"
                          >
                            {{ isBookingRestaurantExpanded(fan._id, r.key) ? 'mdi-chevron-up' : 'mdi-chevron-down' }}
                          </VIcon>
                        </button>
                        <VExpandTransition>
                          <div
                            v-if="isBookingRestaurantExpanded(fan._id, r.key)"
                            class="admin-fans__bookings-rows"
                          >
                            <div
                              v-for="(b, idx) in r.bookings"
                              :key="idx"
                              class="admin-fans__bookings-row"
                            >
                              <span class="admin-fans__bookings-row-date">
                                {{ b.date ? moment(b.date).format('MM-DD-YY') : '—' }}
                              </span>
                              <span
                                v-if="b.partySize"
                                class="admin-fans__bookings-row-party"
                              >Party of {{ b.partySize }}</span>
                              <span
                                v-else
                                class="admin-fans__bookings-row-party"
                              />
                              <span class="admin-fans__bookings-row-spend">
                                {{ formatCurrency(b.spend) }}
                              </span>
                            </div>
                          </div>
                        </VExpandTransition>
                      </div>
                    </template>
                  </div>
                </VExpandTransition>
              </template>
              <!--
                Under Bookings via RM (Kapil, 2026-09-25): their highlight, or
                the filled note icon when they keep a note about you privately.
              -->
              <FanNoteLine
                :fan="fan"
                :restaurant-name="noteRestaurantName"
                :highlight-restaurant-name="highlightRestaurantName(fan)"
              />
            </div>

            <!-- Bottom action row -->
            <div class="admin-fans__toggle-row admin-fans__action-row">
              <button
                type="button"
                class="admin-fans__profile-toggle admin-fans__preferred-toggle"
                :class="{ 'admin-fans__preferred-toggle--active': fan.isPreferred }"
                :aria-label="fan.isPreferred ? 'Manage Preferred status' : 'Mark as Preferred'"
                @click.stop="openPreferredDialog(fan)"
              >
                <VIcon size="14">
                  {{ fan.isPreferred ? 'mdi-star' : 'mdi-star-outline' }}
                </VIcon>
                <span>{{ fan.isPreferred ? 'Preferred' : 'Mark Preferred' }}</span>
              </button>
              <button
                v-if="messagingEligibleRestaurantsFor(fan).length > 0"
                type="button"
                class="admin-fans__profile-toggle admin-fans__message-toggle"
                aria-label="Send message"
                @click.stop="openMessageDialog(fan)"
              >
                <VIcon size="16">
                  mdi-message-text-outline
                </VIcon>
              </button>
              <button
                v-if="messagingEligibleRestaurantsFor(fan).length > 0"
                type="button"
                class="admin-fans__profile-toggle admin-fans__notes-toggle"
                aria-label="Manage guest notes"
                @click.stop="openGuestNotesDialog(fan)"
              >
                <VIcon size="16">
                  {{ (fan.guestNoteCount || 0) > 0 ? 'mdi-note-text' : 'mdi-note-text-outline' }}
                </VIcon>
                <span>Guest notes</span>
                <span
                  v-if="(fan.guestNoteCount || 0) > 0"
                  class="admin-fans__notes-count"
                >
                  {{ fan.guestNoteCount }}
                </span>
              </button>
            </div>
          </template>
        </div>
      </div>
    </div>

    <!-- Bulk message dialog -->
    <BulkMessageDialog
      v-if="bulkMessageOpen"
      :fans="bulkMessageFans"
      :restaurants="restaurants"
      :restaurant-id="filters.restaurantId"
      @close="bulkMessageOpen = false"
      @sent="() => { bulkMessageOpen = false; fansStore.clearSelection() }"
    />

    <!-- Load more -->
    <div
      v-if="hasMore && !loading"
      class="admin-fans__load-more"
    >
      <VBtn
        variant="outlined"
        color="primary"
        @click="fansStore.loadMore()"
      >
        Load more
      </VBtn>
    </div>
    <div
      v-if="loading && fans.length > 0"
      class="admin-fans__load-more"
    >
      <VProgressCircular
        indeterminate
        color="primary"
        size="24"
      />
    </div>

    <!-- Preferred toggle dialog -->
    <PreferredMemberDialog
      v-model="preferredDialogOpen"
      :member="preferredDialogFan"
      @preferred-updated="onPreferredUpdated"
    />

    <!-- Restaurant picker bottom sheet -->
    <VBottomSheet v-model="restaurantPickerOpen">
      <VCard>
        <VCardTitle
          class="pt-4 pb-2"
          style="font-size: 16px;"
        >
          Filter by restaurant
        </VCardTitle>
        <VList density="compact">
          <VListItem
            title="All restaurants"
            :active="!filters.restaurantId"
            active-color="primary"
            @click="setRestaurant(null)"
          />
          <VListItem
            v-for="r in restaurants"
            :key="r._id"
            :title="r.name"
            :subtitle="r.market || undefined"
            :active="filters.restaurantId === r._id"
            active-color="primary"
            @click="setRestaurant(r._id)"
          />
        </VList>
        <VCardActions class="pb-4">
          <VSpacer />
          <VBtn
            variant="text"
            @click="restaurantPickerOpen = false"
          >
            Cancel
          </VBtn>
        </VCardActions>
      </VCard>
    </VBottomSheet>

    <!-- Message composer dialog -->
    <VDialog
      v-model="messageDialogOpen"
      :width="dialogWidth('sm')"
      :max-width="500"
    >
      <VCard>
        <VCardTitle
          class="pt-4 px-4"
          style="font-size: 16px; font-weight: 600;"
        >
          Send message to {{ messageDialogFan?.firstName }} {{ messageDialogFan?.lastName }}
        </VCardTitle>
        <VCardText>
          <div
            v-if="messageHistoryLoading"
            class="admin-fans__history-loading"
          >
            <VProgressCircular
              indeterminate
              color="primary"
              size="24"
            />
          </div>
          <div
            v-else-if="messageHistory.length"
            ref="messageHistoryEl"
            class="admin-fans__history"
          >
            <!-- Only its Fans can be messaged, so the member is one of this restaurant's. -->
            <RaMessageList
              :messages="messageHistory"
              :restaurant-name="messageHistoryRestaurantName"
              member-is-fan
            />
          </div>
          <VSelect
            v-if="dialogEligibleRestaurants.length > 1"
            v-model="selectedRestaurantId"
            :items="dialogEligibleRestaurants"
            item-title="name"
            item-value="_id"
            label="Send as"
            hint="Your message will appear from this restaurant's name."
            persistent-hint
            class="mb-4"
            style="font-size: 16px;"
          />

          <VTextarea
            v-model="messageText"
            label="Message"
            :counter="2000"
            :maxlength="2000"
            rows="4"
            auto-grow
            style="font-size: 16px;"
          />

          <VAlert
            v-if="messageError"
            type="error"
            variant="tonal"
            density="compact"
            class="mt-3"
          >
            {{ messageError }}
          </VAlert>
        </VCardText>
        <VCardActions class="px-4 pb-4">
          <VSpacer />
          <VBtn
            variant="text"
            :disabled="messageSending"
            @click="messageDialogOpen = false"
          >
            Cancel
          </VBtn>
          <VBtn
            color="primary"
            variant="flat"
            :loading="messageSending"
            :disabled="!messageText.trim() || !selectedRestaurantId"
            @click="sendMessage"
          >
            Send
          </VBtn>
        </VCardActions>
      </VCard>
    </VDialog>

    <!-- Guest notes dialog -->
    <VDialog
      v-model="guestNotesDialogOpen"
      :width="dialogWidth('sm')"
      :max-width="600"
      scrollable
    >
      <VCard>
        <VCardTitle
          class="pt-4 px-4"
          style="font-size: 16px; font-weight: 600;"
        >
          Guest notes — {{ guestNotesDialogFan?.firstName }} {{ guestNotesDialogFan?.lastName }}
        </VCardTitle>
        <VCardText style="max-height: 70vh;">
          <p class="admin-fans__notes-sub">
            Visible only to your team — never shown to the member.
          </p>

          <!-- Loading -->
          <div
            v-if="guestNotesLoading"
            class="admin-fans__notes-loading"
          >
            <VProgressCircular
              indeterminate
              size="28"
              color="primary-dark"
            />
          </div>

          <!-- Error -->
          <VAlert
            v-else-if="guestNotesError"
            type="error"
            variant="tonal"
            density="compact"
          >
            {{ guestNotesError }}
          </VAlert>

          <template v-else>
            <!-- Add note affordance -->
            <div
              v-if="restaurantsWithoutGuestNote.length > 0"
              class="admin-fans__notes-add"
            >
              <VBtn
                v-if="restaurantsWithoutGuestNote.length === 1"
                variant="text"
                color="primary"
                size="small"
                prepend-icon="mdi-plus"
                @click="beginAddingGuestNote(restaurantsWithoutGuestNote[0]._id)"
              >
                Add note
              </VBtn>
              <VMenu
                v-else
                v-model="guestNoteAddMenuOpen"
                location="bottom end"
              >
                <template #activator="{ props }">
                  <VBtn
                    v-bind="props"
                    variant="text"
                    color="primary"
                    size="small"
                    prepend-icon="mdi-plus"
                    append-icon="mdi-menu-down"
                  >
                    Add note
                  </VBtn>
                </template>
                <VList
                  density="compact"
                  min-width="220"
                >
                  <VListItem
                    v-for="r in restaurantsWithoutGuestNote"
                    :key="r._id"
                    :title="r.name"
                    @click="beginAddingGuestNote(r._id)"
                  />
                </VList>
              </VMenu>
            </div>

            <p
              v-if="visibleGuestNoteRows.length === 0"
              class="admin-fans__notes-empty"
            >
              No guest notes yet.
            </p>

            <div
              v-else
              class="admin-fans__notes-list"
            >
              <div
                v-for="r in visibleGuestNoteRows"
                :key="r._id"
                class="admin-fans__note-row"
              >
                <!-- EDIT MODE -->
                <div
                  v-if="editingGuestNotes[r._id]"
                  class="admin-fans__note-edit"
                >
                  <div class="admin-fans__note-restaurant">
                    {{ r.name }}
                  </div>
                  <VTextarea
                    v-model="guestNoteDrafts[r._id]"
                    :counter="GUEST_NOTE_MAX_LENGTH"
                    :maxlength="GUEST_NOTE_MAX_LENGTH"
                    rows="3"
                    auto-grow
                    variant="outlined"
                    density="comfortable"
                    hide-details="auto"
                    placeholder="Add a note about this guest…"
                    autofocus
                    style="font-size: 16px;"
                  />
                  <VAlert
                    v-if="guestNoteRowError[r._id]"
                    type="error"
                    variant="tonal"
                    density="compact"
                    class="mt-2"
                  >
                    {{ guestNoteRowError[r._id] }}
                  </VAlert>
                  <div class="admin-fans__note-edit-actions">
                    <div class="admin-fans__note-meta">
                      <template v-if="guestNoteFor(r._id)">
                        Last updated by
                        {{ guestNoteFor(r._id).updatedByName || 'a team member' }}
                        · {{ formatNoteUpdated(guestNoteFor(r._id).updatedAt) }}
                      </template>
                    </div>
                    <div class="admin-fans__note-edit-buttons">
                      <VBtn
                        v-if="guestNoteFor(r._id)"
                        variant="text"
                        color="error"
                        size="small"
                        :disabled="guestNoteSaving[r._id]"
                        @click="removeGuestNote(r._id)"
                      >
                        Remove
                      </VBtn>
                      <VBtn
                        variant="text"
                        size="small"
                        :disabled="guestNoteSaving[r._id]"
                        @click="cancelEditingGuestNote(r._id)"
                      >
                        Cancel
                      </VBtn>
                      <VBtn
                        color="primary"
                        variant="flat"
                        size="small"
                        :loading="guestNoteSaving[r._id]"
                        :disabled="!guestNoteHasChange(r._id) || !(guestNoteDrafts[r._id] || '').trim()"
                        @click="saveGuestNote(r._id)"
                      >
                        Save
                      </VBtn>
                    </div>
                  </div>
                </div>

                <!-- READ MODE -->
                <button
                  v-else
                  type="button"
                  class="admin-fans__note-read"
                  @click="startEditingGuestNote(r._id)"
                >
                  <div class="admin-fans__note-read-head">
                    <span class="admin-fans__note-restaurant">{{ r.name }}</span>
                    <span class="admin-fans__note-meta">
                      {{ guestNoteFor(r._id).updatedByName || 'a team member' }}
                      · {{ formatNoteUpdated(guestNoteFor(r._id).updatedAt) }}
                    </span>
                  </div>
                  <div class="admin-fans__note-read-body">
                    {{ guestNoteFor(r._id).note }}
                  </div>
                </button>
              </div>
            </div>
          </template>
        </VCardText>
        <VCardActions class="px-4 pb-4">
          <VSpacer />
          <VBtn
            variant="text"
            @click="guestNotesDialogOpen = false"
          >
            Close
          </VBtn>
        </VCardActions>
      </VCard>
    </VDialog>
  </div>
</template>

<style scoped>
.admin-fans {
  max-width: 900px;
  margin: 0 auto;
  padding: var(--ds-space-6, 24px) var(--ds-space-4, 16px);
}

@media (max-width: 600px) {
  .admin-fans {
    padding: var(--ds-space-4, 16px) var(--ds-space-3, 12px);
  }
}

.admin-fans__search {
  max-width: 480px;
  margin: 0 auto var(--ds-space-5, 20px);
}

/* Pin the search field to one fixed shade. Vuetify's filled-variant overlay
   otherwise darkens the field on focus, making the resting state look lighter
   than the focused state — here resting = hover = focused, all at the darker
   (focused) tone. */
.admin-fans__search :deep(.v-field) {
  background-color: #d5d5d5;
}

.admin-fans__search :deep(.v-field__overlay) {
  opacity: 0;
}

.admin-fans__chips {
  display: flex;
  gap: var(--ds-space-2, 8px);
  flex-wrap: wrap;
  margin-bottom: var(--ds-space-3, 12px);
}

.admin-fans__chips--restaurant {
  width: 100%;
  justify-content: flex-start;
}

.admin-fans__chip {
  cursor: pointer;
  font-size: 14px;
}
.admin-fans_main_chip {
  font-size: 16px;
}
.admin-fans__sort-btn {
  font-size: 14px;
  color: rgba(var(--v-theme-on-surface), var(--v-medium-emphasis-opacity));
}

.admin-fans__grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: var(--ds-space-3, 12px);
}

@media (min-width: 600px) {
  .admin-fans__grid {
    grid-template-columns: repeat(2, 1fr);
    gap: var(--ds-space-4, 16px);
  }
}

/* ─── Fan card ──────────────────────────────────────────────────────────── */

.admin-fans__card {
  position: relative;
  display: flex;
  flex-direction: column;
  padding: var(--ds-space-4, 16px) var(--ds-space-3, 12px);
  background: #f8f8f9;
  border: 1px solid var(--ds-border-light, #e5e5e5);
  border-radius: 12px;
  cursor: pointer;
  transition: border-color 150ms ease, box-shadow 150ms ease, transform 150ms ease;
  outline: none;
  min-width: 0;
  overflow: hidden;
}

@media (min-width: 600px) {
  .admin-fans__card {
    padding: var(--ds-space-5, 20px) var(--ds-space-4, 16px) var(--ds-space-4, 16px);
  }
}

.admin-fans__card:hover,
.admin-fans__card:focus-visible {
  border-color: var(--ds-brand-primary);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.06);
}

.admin-fans__card:focus-visible {
  box-shadow: 0 0 0 3px rgba(69, 182, 169, 0.25);
}

.admin-fans__card--skeleton {
  pointer-events: none;
}

.admin-fans__card--selected {
  border-color: var(--ds-brand-primary);
  background: var(--ds-brand-primary-light, #e9f4f3);
}

/* ─── Selection checkbox ──────────────────────────────────────────────────── */

.admin-fans__checkbox {
  position: absolute;
  top: 2px;
  right: 2px;
  z-index: 2;
}

/* Keep the compact member block clear of the checkbox. */
.admin-fans__member-compact {
  padding-right: 32px;
}

/* ─── Bulk message action ─────────────────────────────────────────────────── */

.admin-fans__bulk-action {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  margin-bottom: var(--ds-space-3, 12px);
}

.admin-fans__bulk-action-row {
  display: flex;
  align-items: center;
  gap: var(--ds-space-2, 8px);
}

.admin-fans__bulk-action-clear {
  font-size: 13px;
}

.admin-fans__bulk-action-hint {
  margin: var(--ds-space-2, 8px) 0 0;
  font-size: 13px;
  color: rgba(var(--v-theme-on-surface), var(--v-medium-emphasis-opacity));
}

.admin-fans__bulk-action-error {
  margin-top: var(--ds-space-2, 8px);
  font-size: 14px;
}

.admin-fans__card-body {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: var(--ds-space-2, 8px);
  flex: 1;
}

.admin-fans__avatar-wrap {
  display: flex;
  justify-content: center;
  margin-bottom: var(--ds-space-2, 8px);
}

.admin-fans__avatar {
  outline: 3px solid rgba(69, 182, 169, 0.25);
  outline-offset: 3px;
}

.admin-fans__avatar-initials {
  font-size: 22px;
  font-weight: 600;
  color: var(--ds-brand-primary-dark);
  letter-spacing: 0.5px;
}

.admin-fans__name {
  font-size: 18px;
  font-weight: 700;
  color: var(--ds-text-strong, #1a1a1a);
  line-height: 1.3;
  margin: 0;
  word-break: break-word;
}

.admin-fans__chips {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  align-items: center;
  gap: 4px;
}

.admin-fans__tag-more {
  font-size: 11px;
  color: rgba(var(--v-theme-on-surface), var(--v-medium-emphasis-opacity));
}

.admin-fans__role {
  font-size: 14px;
  font-weight: 500;
  color: #555;
  line-height: 1.4;
  margin: 0;
  word-break: break-word;
}

.admin-fans__meta {
  font-size: 12px;
  color: #777;
  margin: 0;
  align-self: flex-start;
}

.admin-fans__meta-group {
  display: flex;
  flex-direction: column;
  gap: 2px;
  align-self: stretch;
  width: 100%;
}

.admin-fans__bookings-trigger {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  align-self: flex-start;
  padding: 2px 2px 2px 0;
  margin: 0;
  background: transparent;
  border: 0;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
  color: var(--ds-brand-primary-darker, #0FA091);
  cursor: pointer;
  transition: color 150ms ease, background 150ms ease;
}

.admin-fans__bookings-trigger:hover,
.admin-fans__bookings-trigger:focus-visible {
  background: var(--ds-brand-primary-light, #e9f4f3);
  outline: none;
}

.admin-fans__bookings-panel {
  margin-top: var(--ds-space-2, 8px);
  padding: var(--ds-space-3, 12px);
  background: #fff;
  border: 1px solid var(--ds-border-light, #e5e5e5);
  border-radius: 8px;
  font-size: 12px;
  color: #333;
}

.admin-fans__bookings-loading {
  display: flex;
  justify-content: center;
  padding: var(--ds-space-3, 12px) 0;
}

.admin-fans__bookings-error {
  color: rgb(var(--v-theme-error, 211, 47, 47));
  font-size: 12px;
}

.admin-fans__bookings-summary {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--ds-space-3, 12px);
  padding-bottom: var(--ds-space-3, 12px);
  margin-bottom: var(--ds-space-2, 8px);
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
}

.admin-fans__bookings-stat-value {
  font-size: 16px;
  font-weight: 600;
  color: var(--ds-brand-primary-dark, #3F998F);
  line-height: 1.2;
}

.admin-fans__bookings-stat-label {
  margin-top: 2px;
  font-size: 11px;
  color: rgba(var(--v-theme-on-surface), var(--v-medium-emphasis-opacity));
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.admin-fans__bookings-restaurant {
  padding: 6px 0;
  border-bottom: 1px solid rgba(0, 0, 0, 0.04);
}

.admin-fans__bookings-restaurant:last-child {
  border-bottom: none;
}

.admin-fans__bookings-restaurant-header {
  display: flex;
  align-items: center;
  gap: var(--ds-space-2, 8px);
  width: 100%;
  padding: 4px 0;
  background: transparent;
  border: 0;
  cursor: pointer;
  text-align: left;
  color: inherit;
  border-radius: 4px;
  transition: background 150ms ease;
}

.admin-fans__bookings-restaurant-header:hover,
.admin-fans__bookings-restaurant-header:focus-visible {
  background: var(--ds-brand-primary-light, #e9f4f3);
  outline: none;
}

.admin-fans__bookings-restaurant-info {
  flex: 1;
  min-width: 0;
}

.admin-fans__bookings-restaurant-name {
  font-size: 13px;
  font-weight: 600;
  color: #1a1a1a;
  word-break: break-word;
}

.admin-fans__bookings-restaurant-market {
  font-size: 11px;
  color: rgba(var(--v-theme-on-surface), var(--v-medium-emphasis-opacity));
  margin-top: 1px;
}

.admin-fans__bookings-restaurant-totals {
  text-align: right;
  flex-shrink: 0;
  min-width: 80px;
}

.admin-fans__bookings-restaurant-count {
  font-size: 12px;
  color: #333;
}

.admin-fans__bookings-restaurant-spend {
  font-size: 11px;
  color: rgba(var(--v-theme-on-surface), var(--v-medium-emphasis-opacity));
  margin-top: 1px;
}

.admin-fans__bookings-restaurant-chevron {
  color: rgba(var(--v-theme-on-surface), var(--v-medium-emphasis-opacity));
  flex-shrink: 0;
}

.admin-fans__bookings-rows {
  padding: 4px 0 4px 4px;
}

.admin-fans__bookings-row {
  display: grid;
  grid-template-columns: 70px 1fr 70px;
  align-items: center;
  gap: 8px;
  padding: 4px 0;
  font-size: 12px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.04);
}

.admin-fans__bookings-row:last-child {
  border-bottom: none;
}

.admin-fans__bookings-row-date {
  color: rgba(var(--v-theme-on-surface), var(--v-medium-emphasis-opacity));
}

.admin-fans__bookings-row-party {
  text-align: center;
  color: #333;
}

.admin-fans__bookings-row-spend {
  text-align: right;
  color: #333;
}

/* ─── Compact member block (mirrors rcadmin__member in reservations-review) ── */

.admin-fans__member-compact {
  display: flex;
  align-items: flex-start;
  gap: var(--ds-space-2, 8px);
  width: 100%;
  align-self: stretch;
  text-align: left;
  font-size: 13px;
  color: #595959;
}

.admin-fans__avatar-slot {
  display: flex;
  flex-shrink: 0;
  text-decoration: none;
  border-radius: 50%;
  transition: box-shadow 150ms ease;
}

a.admin-fans__avatar-slot:hover,
a.admin-fans__avatar-slot:focus-visible {
  outline: none;
  box-shadow: 0 0 0 2px var(--ds-brand-primary-light), 0 0 0 3px var(--ds-brand-primary);
}

.admin-fans__avatar-small--initials {
  background: var(--ds-brand-primary-light, #e9f4f3);
  outline: thin solid rgba(69, 182, 169, 0.25);
  outline-offset: 2px;
}

.admin-fans__avatar-small-text {
  font-size: 13px;
  font-weight: 600;
  color: var(--ds-brand-primary-darker, #0FA091);
}

.admin-fans__member-info {
  min-width: 0;
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.admin-fans__member-name-row {
  line-height: 1.3;
  color: #1a1a1a;
  font-weight: 600;
  word-break: break-word;
  min-width: 0;
}

.admin-fans__member-link,
.admin-fans__member-name-static {
  color: inherit;
  text-decoration: none;
  transition: color 150ms ease;
}

.admin-fans__member-link:hover,
.admin-fans__member-link:focus-visible {
  color: var(--ds-brand-primary-darker, #0FA091);
  text-decoration: underline;
  text-underline-offset: 2px;
  outline: none;
}

.admin-fans__member-badges {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-left: 6px;
  vertical-align: middle;
  white-space: nowrap;
}

.admin-fans__member-verified {
  color: rgb(0, 149, 246);
  margin-top: -1px;
}

.admin-fans__platinum-label {
  font-size: 12px;
  font-weight: 500;
  color: inherit;
}

.admin-fans__member-subtitle {
  font-size: 12px;
  color: #777;
  line-height: 1.4;
  font-weight: 400;
  white-space: normal;
  word-break: break-word;
  overflow-wrap: anywhere;
}

/* ─── Contact row (matches rcadmin__contact) ──────────────────────────────── */

.admin-fans__contact {
  display: flex;
  flex-wrap: wrap;
  gap: 4px 12px;
  padding: 8px 12px;
  width: 100%;
  align-self: stretch;
  background: #fff;
  border: 1px solid var(--ds-border-light, #e5e5e5);
  border-radius: 8px;
  font-size: 12px;
}

.admin-fans__contact-item {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: #595959;
  text-decoration: none;
  transition: color 150ms ease;
  max-width: 100%;
  min-width: 0;
  word-break: break-all;
  overflow-wrap: anywhere;
}

.admin-fans__contact-item > span {
  min-width: 0;
  overflow-wrap: anywhere;
  word-break: break-all;
}

.admin-fans__contact-item:hover {
  color: var(--ds-brand-primary-darker, #0FA091);
}

.admin-fans__contact-icon {
  color: #777;
  flex-shrink: 0;
}

/* ─── View full profile toggle (pill) ─────────────────────────────────────── */

.admin-fans__toggle-row {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  align-items: center;
  margin-top: var(--ds-space-2, 8px);
}

.admin-fans__profile-toggle {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  align-self: flex-start;
  padding: 4px 10px 4px 12px;
  background: transparent;
  border: 1px solid var(--ds-border-light, #e5e5e5);
  border-radius: 999px;
  font-size: 12px;
  font-weight: 500;
  color: #595959;
  cursor: pointer;
  transition: all 150ms ease;
}

.admin-fans__toggle-row > .admin-fans__profile-toggle {
  margin-top: 0;
}

.admin-fans__profile-toggle:hover,
.admin-fans__profile-toggle:focus-visible {
  background: var(--ds-brand-primary-light, #e9f4f3);
  border-color: var(--ds-brand-primary);
  color: var(--ds-brand-primary-darker, #0FA091);
  outline: none;
}

.admin-fans__preferred-toggle {
  padding: 4px 12px 4px 10px;
}

.admin-fans__preferred-toggle--active {
  background: var(--ds-brand-primary-dark, #3F998F);
  border-color: var(--ds-brand-primary-dark, #3F998F);
  color: #fff;
}

.admin-fans__preferred-toggle--active:hover,
.admin-fans__preferred-toggle--active:focus-visible {
  background: var(--ds-brand-primary-darker, #0FA091);
  border-color: var(--ds-brand-primary-darker, #0FA091);
  color: #fff;
}

.admin-fans__message-toggle {
  padding: 4px 8px;
  border-color: var(--ds-brand-primary, #45B6A9);
  color: var(--ds-brand-primary-darker, #0FA091);
}

.admin-fans__message-toggle:hover,
.admin-fans__message-toggle:focus-visible {
  background: var(--ds-brand-primary-darker, #0FA091);
  border-color: var(--ds-brand-primary-darker, #0FA091);
  color: #fff;
}

.admin-fans__notes-toggle {
  border-color: var(--ds-brand-primary, #45B6A9);
  color: var(--ds-brand-primary-darker, #0FA091);
}

.admin-fans__notes-toggle:hover,
.admin-fans__notes-toggle:focus-visible {
  background: var(--ds-brand-primary-darker, #0FA091);
  border-color: var(--ds-brand-primary-darker, #0FA091);
  color: #fff;
}

/* Count badge shown on the Guest notes button when the fan has notes. */
.admin-fans__notes-count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  border-radius: 9px;
  background: var(--ds-brand-primary-darker, #0FA091);
  color: #fff;
  font-size: 11px;
  font-weight: 700;
  line-height: 1;
}

.admin-fans__notes-toggle:hover .admin-fans__notes-count,
.admin-fans__notes-toggle:focus-visible .admin-fans__notes-count {
  background: #fff;
  color: var(--ds-brand-primary-darker, #0FA091);
}

/* Bottom-pinned action row holding Mark Preferred / Message / Guest notes. */
.admin-fans__action-row {
  margin-top: auto;
  padding-top: var(--ds-space-2, 8px);
  border-top: 1px solid rgba(0, 0, 0, 0.06);
}

/* ─── Guest notes dialog ──────────────────────────────────────────────────── */

.admin-fans__notes-loading {
  display: flex;
  justify-content: center;
  padding: var(--ds-space-4, 16px) 0;
}

.admin-fans__notes-sub {
  font-size: 12px;
  color: rgba(var(--v-theme-on-surface), var(--v-medium-emphasis-opacity));
  margin: 0 0 var(--ds-space-3, 12px);
}

.admin-fans__notes-add {
  margin-bottom: var(--ds-space-2, 8px);
}

.admin-fans__notes-empty {
  font-size: 14px;
  color: rgba(var(--v-theme-on-surface), var(--v-medium-emphasis-opacity));
  padding: var(--ds-space-2, 8px) 0;
  margin: 0;
}

.admin-fans__notes-list {
  display: flex;
  flex-direction: column;
  gap: var(--ds-space-2, 8px);
}

.admin-fans__note-restaurant {
  font-size: 14px;
  font-weight: 600;
}

.admin-fans__note-meta {
  font-size: 12px;
  color: rgba(var(--v-theme-on-surface), var(--v-medium-emphasis-opacity));
}

.admin-fans__note-read {
  display: block;
  width: 100%;
  text-align: left;
  background: transparent;
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 8px;
  padding: var(--ds-space-3, 12px);
  cursor: pointer;
  transition: background-color 150ms ease, border-color 150ms ease;
  font: inherit;
  color: inherit;
}

.admin-fans__note-read:hover {
  background-color: rgba(var(--v-theme-on-surface), 0.03);
  border-color: rgba(0, 0, 0, 0.16);
}

.admin-fans__note-read:focus-visible {
  outline: 2px solid var(--ds-brand-primary-dark, currentColor);
  outline-offset: 2px;
}

.admin-fans__note-read-head {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: var(--ds-space-3, 12px);
  margin-bottom: var(--ds-space-1, 4px);
  flex-wrap: wrap;
}

.admin-fans__note-read-body {
  font-size: 14px;
  line-height: 1.45;
  color: rgba(var(--v-theme-on-surface), 0.85);
  white-space: pre-wrap;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
}

.admin-fans__note-edit {
  background: rgba(var(--v-theme-on-surface), 0.02);
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 8px;
  padding: var(--ds-space-3, 12px);
}

.admin-fans__note-edit .admin-fans__note-restaurant {
  margin-bottom: var(--ds-space-2, 8px);
}

.admin-fans__note-edit-actions {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--ds-space-3, 12px);
  margin-top: var(--ds-space-2, 8px);
  flex-wrap: wrap;
}

.admin-fans__note-edit-buttons {
  display: flex;
  gap: var(--ds-space-1, 4px);
}

.admin-fans__profile-loading {
  display: flex;
  justify-content: center;
  padding: var(--ds-space-6, 24px) 0;
}

.admin-fans__profile-expanded {
  width: 100%;
}

/* ─── Skeleton helpers ──────────────────────────────────────────────────── */

.admin-fans__skeleton-line {
  width: 100%;
}

.admin-fans__empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: var(--ds-space-16, 64px) var(--ds-space-4, 16px);
  text-align: center;
}

.admin-fans__empty-title {
  font-size: var(--ds-text-lg, 20px);
  font-weight: 600;
  color: var(--ds-text-strong, inherit);
  margin-bottom: var(--ds-space-2, 8px);
}

.admin-fans__empty-sub {
  font-size: var(--ds-text-base, 16px);
  color: rgba(var(--v-theme-on-surface), var(--v-medium-emphasis-opacity));
  max-width: 320px;
}

.admin-fans__load-more {
  display: flex;
  justify-content: center;
  padding: var(--ds-space-6, 24px) 0;
}

/* The conversation so far, above the message box — newest at the bottom. */
.admin-fans__history {
  max-height: 280px;
  overflow-y: auto;
  margin-bottom: 16px;
  padding-bottom: 12px;
  border-bottom: 1px solid var(--ds-border-default, rgba(var(--v-theme-on-surface), 0.12));
}

.admin-fans__history-loading {
  display: flex;
  justify-content: center;
  padding: 12px 0 20px;
}
</style>

<route lang="yaml">
meta:
  layout: website
  action: read
  subject: Auth
  allowLoggedInOnly: true
  requiresRestaurantAdmin: true
</route>
